import Cookies from 'universal-cookie';
import { v4 as uuidv4 } from 'uuid';
import { apiQueryClient } from '../../App';
import { useQuery } from '@tanstack/react-query';
import { errors } from '../constants'
import { sessionMatchesCustomer } from './sessionIdentity'

const BFF_BASE_URL = process.env.REACT_APP_BFF_BASE_URL

const cookies = new Cookies({
    path: '/'
  });

function isValidAuth(authData: any) {
    if(!authData?.tokens?.expiresIn || 
      !authData?.tokens?.accessToken || 
      !authData?.tokens?.idToken || 
      !authData?.tokens?.refreshToken || 
      !authData?.tokens?.sessionId
    ) {
      throw new Error('Session data is not valid')
    }
    const expiryDate = new Date(authData?.tokens?.expiresAt)
    if(expiryDate < new Date()) {
        throw new Error('Session data is expired')
    }
    return true;
}

function saveTokens(authData: any) {
    isValidAuth(authData)
    return authData;
} 

function removeUserSession() {
    apiQueryClient.invalidateQueries()
    cookies.remove('access_token');
    cookies.remove('id_token');
    cookies.remove('refresh_token');
    cookies.remove('session_id');
    cookies.remove('expiry_millis');
    return { remember: false } as any 
}

export async function signInEmail(username: string, password: string, rememberMe=false) {
    removeUserSession();
    cookies.remove('staff');
    const res = await fetch(`${BFF_BASE_URL}/v1/auth/signin/email`,
        {
            method: 'POST',
            body: JSON.stringify({ username, password, rememberMe }),
            headers: {
                'Content-Type': 'application/json',
                'x-correlation-id': uuidv4()
            },
            credentials: 'include'
        }
    );
    
    if (!res.ok) {
        removeUserSession();
        let message = (await res.json()).message
        if(!errors[message]) {
            message='Internal Server Error'
        }
        throw new Error(message);
    }
    return saveTokens(await res.json())

}

export async function getCurrentSession() {
    try {
        const res = await fetch(`${BFF_BASE_URL}/v1/auth/current/session`,{
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'x-correlation-id': uuidv4()
            },
            credentials: 'include'
        });

        if (!res.ok) {
            return signOutUser()
        }
        const tokens = await res.json()
        if (!sessionMatchesCustomer(tokens, apiQueryClient.getQueryData(['Customer']))) {
            // Signed in as someone else in another tab: reload to show that customer, and send
            // nothing with the old customer's siteId in the meantime.
            window.location.reload()
            return {}
        }
        return tokens
    } catch {
        return signOutUser()
    }
}

export async function validatePassword(username: string, password: string) {
    try {
        const res = await fetch(`${BFF_BASE_URL}/v1/auth/signin/username`,
            {
                method: 'POST',
                body: JSON.stringify({ username, password }),
                headers: {
                    'Content-Type': 'application/json',
                    'x-correlation-id': uuidv4()
                }
            }
        );
        if (!res.ok) {
            throw new Error((await res.json()).message);
        }
        return isValidAuth(await res.json())
    } catch {
        throw new Error('Oops! Something happen on backend services. Pls try again later.')
    }
}

export async function signInOtp(codeVerifier: string, referenceId: string, otp: string) {
    removeUserSession();
    const res = await fetch(`${BFF_BASE_URL}/v1/auth/signin/otp`,
        {
            method: 'POST',
            body: JSON.stringify({  codeVerifier, referenceId, otp  }),
            headers: {
                'Content-Type': 'application/json',
                'x-correlation-id': uuidv4()
            },
            credentials: 'include'
        }
    );
    if (!res.ok) {
        removeUserSession();
        throw new Error((await res.json()).message);
    }
    return saveTokens(await res.json())
}

export async function refreshSession(idToken: string, refreshToken: string) {
    try {
        const res = await fetch(`${BFF_BASE_URL}/v1/auth/refresh`,
            {
                method: 'POST',
                body: JSON.stringify({ idToken, refreshToken }),
                headers: {
                    'Content-Type': 'application/json',
                    'x-correlation-id': uuidv4()
                }
            }
        );
        if (!res.ok) {
            removeUserSession();
            // throw new Error((await res.json()).message);
        }
        return saveTokens(await res.json())
    } catch {
        console.log('API Error')
    }
    return null;
}

export async function requestOtp(username: string, action='emailVerification') {
    const res = await fetch(`${BFF_BASE_URL}/v1/users/otp/request`,
        {
            method: 'POST',
            body: JSON.stringify({ username, type:'email', action }),
            headers: {
                'Content-Type': 'application/json',
                'x-correlation-id': uuidv4()
            }
        }
    );
    if (!res.ok) {
        let message = (await res.json()).message
        if(message?.indexOf('does not exist')>-1) {
            message = 'Wrong email'
        }
        throw new Error(message);
    }
    return (await res.json())

}

export async function getAuthorizationCode(codeVerifier: string, referenceId: string, otp: string) {
    const res = await fetch(`${BFF_BASE_URL}/v1/users/auth/code`,
        {
            method: 'POST',
            body: JSON.stringify({  codeVerifier, referenceId, otp  }), 
            headers: {
                'Content-Type': 'application/json',
                'x-correlation-id': uuidv4()
            }
        }
    );
    if (!res.ok) {
        throw new Error((await res.json()).message);
    }
    return (await res.json())
}

export async function changePassword(authorizationCode: string, password: string) {
    const res = await fetch(`${BFF_BASE_URL}/v1/users/password/change`,
        {
            method: 'POST',
            body: JSON.stringify({  authorizationCode, password  }),
            headers: {
                'Content-Type': 'application/json',
                'x-correlation-id': uuidv4()
            }
        }
    );
    if (!res.ok) {
        throw new Error((await res.json()).message);
    }
    return (await res.json())
}

export async function revokeSession(refreshToken?: string) {
    try {
        await fetch(`${BFF_BASE_URL}/v1/auth/revoke`,
            {
                method: 'POST',
                body: JSON.stringify({ refreshToken }),
                headers: {
                    'Content-Type': 'application/json',
                    'x-correlation-id': uuidv4()
                },
                credentials: 'include'
            }
        );
    } catch {
        // 
    }
}

export async function signOutUser() {
    cookies.remove('staff');
    removeUserSession()
    revokeSession() 
    return {}
}

export function impersonationStaff(staff?: string) {
    if(staff) cookies.set('staff', staff);
    return  cookies.get('staff');
}

export const useGetCurrentSession = () => {
    const { isPending, 
            error: errorSession, 
            data: currentSession, 
            refetch: refetchSession 
        } = useQuery( {
        queryKey: ['CurrentSession'], // key must be unique
        queryFn: getCurrentSession,
        staleTime: 60000
    })
    return { isPending, errorSession , currentSession, refetchSession }
};


